import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Menu, LogOut, Settings, Lightbulb, Calendar, BookOpen, GraduationCap, Briefcase, Gift, Tag, ChevronDown, Compass, Globe, Building2, User } from 'lucide-react';
import NavLink from './NavLink';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { NetworkingNotificationBadge } from '../networking/NetworkingNotificationBadge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";

interface MobileNavigationProps {
  isHomePage: boolean;
  isScrolled: boolean;
  user: any;
  member: any;
  isAdmin: boolean;
  isMember: boolean;
  logout: () => void;
}

const MobileNavigation: React.FC<MobileNavigationProps> = React.memo(({ isHomePage, isScrolled, user, member, isAdmin, isMember, logout }) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = React.useState(false);

  const handleJoin = () => {
    navigate('/member-access');
  };

  const handleLogout = () => {
    logout();
  };
  
  return (
    <div>
      <Sheet>
        <SheetTrigger asChild>
          <button className="md:hidden text-white">
            <Menu size={24} />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-full p-0 max-w-xs bg-background border-border">
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <Link to="/" className="font-bold text-2xl text-[#FFCF43]">First Tuesday</Link>
              <LanguageSwitcher />
            </div>
            
            <div className="flex flex-col space-y-5">
              {/* Visiteurs : Vision & Mission */}
              {!isMember && !isAdmin && (
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-foreground">{t('visionMission')}</h3>
                  <div className="ml-4 flex flex-col space-y-2">
                    {isHomePage ? (
                      <>
                        <SheetClose asChild><NavLink href="#vision" mobile>{t('vision')}</NavLink></SheetClose>
                        <SheetClose asChild><NavLink href="#benefits" mobile>{t('benefits')}</NavLink></SheetClose>
                        <SheetClose asChild><NavLink href="#who" mobile>{t('targetAudience')}</NavLink></SheetClose>
                        <SheetClose asChild><NavLink href="#testimonials" mobile>{t('testimonials')}</NavLink></SheetClose>
                      </>
                    ) : (
                      <>
                        <SheetClose asChild><NavLink to="/#vision" isPage mobile>{t('vision')}</NavLink></SheetClose>
                        <SheetClose asChild><NavLink to="/#benefits" isPage mobile>{t('benefits')}</NavLink></SheetClose>
                        <SheetClose asChild><NavLink to="/#who" isPage mobile>{t('targetAudience')}</NavLink></SheetClose>
                        <SheetClose asChild><NavLink to="/#testimonials" isPage mobile>{t('testimonials')}</NavLink></SheetClose>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Navigation principale — harmonisée avec desktop */}
              <div className="flex flex-col space-y-3">
                {(isMember || isAdmin) && (
                  <SheetClose asChild>
                    <NavLink to="/dashboard" isPage mobile className="font-semibold text-[#FFCF43]">
                      Tableau de bord
                    </NavLink>
                  </SheetClose>
                )}

                <SheetClose asChild>
                  <NavLink to="/members" isPage mobile>Annuaire</NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NetworkingNotificationBadge>
                    <NavLink to="/networking" isPage mobile>Networking</NavLink>
                  </NetworkingNotificationBadge>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/opportunities" isPage mobile>Opportunités</NavLink>
                </SheetClose>

                {/* Section "Explorer" — collapsible catégorisé */}
                <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full font-medium text-foreground py-1">
                    <span className="flex items-center gap-2"><Compass className="h-4 w-4" /> Explorer</span>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", moreOpen && "rotate-180")} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="ml-4 mt-2 flex flex-col space-y-1">
                    {/* Découvrir */}
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-1">Découvrir</span>
                    <SheetClose asChild>
                      <NavLink to="/events" isPage mobile className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" /> Événements
                      </NavLink>
                    </SheetClose>
                    <SheetClose asChild>
                      <NavLink to="/insights" isPage mobile className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-muted-foreground" /> Insights
                      </NavLink>
                    </SheetClose>
                    <SheetClose asChild>
                      <NavLink to="/workshops" isPage mobile className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-muted-foreground" /> Workshops
                      </NavLink>
                    </SheetClose>

                    {/* Carrière */}
                    <div className="border-t border-border my-1" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-1">Carrière</span>
                    <SheetClose asChild>
                      <NavLink to="/jobs" isPage mobile className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-muted-foreground" /> Job Board
                      </NavLink>
                    </SheetClose>
                    <SheetClose asChild>
                      <NavLink to="/formation" isPage mobile className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-muted-foreground" /> Formation
                      </NavLink>
                    </SheetClose>

                    {/* Business (membres) */}
                    {(isMember || isAdmin) && (
                      <>
                        <div className="border-t border-border my-1" />
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-1">Business</span>
                        <SheetClose asChild>
                          <NavLink to="/deals" isPage mobile className="flex items-center gap-2">
                            <Tag className="h-4 w-4 text-muted-foreground" /> Deals
                          </NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink to="/perks" isPage mobile className="flex items-center gap-2">
                            <Gift className="h-4 w-4 text-muted-foreground" /> Avantages
                          </NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink to="/commissions" isPage mobile className="flex items-center gap-2">
                            <Lightbulb className="h-4 w-4 text-muted-foreground" /> Commissions
                          </NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink to="/business" isPage mobile className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" /> Business
                          </NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink to="/international" isPage mobile className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-muted-foreground" /> International
                          </NavLink>
                        </SheetClose>
                      </>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Compte — liens perso en mobile */}
                {user && member && (
                  <>
                    <div className="border-t border-border my-2" />
                    <SheetClose asChild>
                      <NavLink to="/profile" isPage mobile className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" /> Mon Profil
                      </NavLink>
                    </SheetClose>
                    <SheetClose asChild>
                      <NavLink to="/subscription" isPage mobile className="flex items-center gap-2">
                        <Settings className="h-4 w-4 text-muted-foreground" /> Mon Abonnement
                      </NavLink>
                    </SheetClose>
                  </>
                )}

                {isAdmin && (
                  <SheetClose asChild>
                    <NavLink 
                      to="/admin" 
                      isPage 
                      mobile 
                      className="flex items-center gap-2 text-[#FFCF43] hover:underline"
                    >
                      <Settings className="h-4 w-4" />
                      Administration
                    </NavLink>
                  </SheetClose>
                )}
              </div>
            </div>

            <div className="flex flex-col space-y-3 mt-6">
              {!user || !member ? (
                <SheetClose asChild>
                  <Button 
                    onClick={handleJoin}
                    className="bg-[#FFCF43] hover:bg-[#FFCF43]/90 text-black font-semibold w-full"
                  >
                    {t('join')}
                  </Button>
                </SheetClose>
              ) : (
                <SheetClose asChild>
                  <Button 
                    onClick={handleLogout}
                    variant="destructive"
                    className="bg-red-500 hover:bg-red-600 text-white w-full"
                  >
                    <LogOut className="mr-1 h-4 w-4" />
                    Déconnexion
                  </Button>
                </SheetClose>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
});

export default MobileNavigation;
